import { useCallback, useRef, useState } from 'react'
import type { ResearchResult, TranscriptItem } from 'types'

import { clientSessionViewStore } from '../../services/clientSessionViewStore.js'
import { useLocaleContext } from '../context/LocaleContext.js'
import type { DiscoveryReportData } from '../core/pdf/utils/discovery-report-types.js'
import {
  ensureSessionCapability,
  SESSION_HEADER,
} from '../core/session/session-identity.js'
import {
  createDiscoveryReportTranscriptItem,
  looksLikeDiscoveryReportPromise,
} from '../../utils/discoveryReportUtils.js'
import { acceptEmbeddedSessionProjection } from '../lib/accept-session-projection-response.js'
import { logger } from '../lib/logger-client.js'
import { cachePreparedDiscoveryReport, clearCachedDiscoveryReport } from '../lib/report-preview-cache.js'
import { downloadDiscoveryReportPdf } from '../lib/report-pdf-download.js'
import {
  DOSSIER_GATE_CODE,
  REPORT_PREPARE_ATTEMPT_TIMEOUT_MS,
  SESSION_VERSION_RETRY_LIMIT,
} from '../core/contracts.js'
import {
  formatReportGateToast,
  readGateMissing,
  ReportDossierGateError,
} from '../lib/report-gate-toast.js'
import type { BriefPrepareTerminal } from '../lib/funnel-agent-label.js'

interface UserProfileLike {
  name: string
  email: string
  company?: string | undefined
  role?: string | undefined
}

interface UseReportGenerationArgs {
  sessionId: string
  transcript: TranscriptItem[]
  userProfile: UserProfileLike | null
  researchResultRef: React.MutableRefObject<ResearchResult | null>
  intelligenceContextRef: React.MutableRefObject<unknown>
  screenShareActive: boolean
  setTranscript: React.Dispatch<React.SetStateAction<TranscriptItem[]>>
  showToast: (message: string, type: 'success' | 'error' | 'info') => void
  handleSendMessage: (message: string) => void
}

export type DiscoveryReportTrigger = 'click' | 'auto' | 'live-tool'

interface SummaryAgentOutput {
  executiveSummary?: string
  keyFindings?: unknown
  recommendedSolution?: string
  solutionRationale?: string
  expectedROI?: string
  pricingBallpark?: string
  nextSteps?: string
  multimodalInteractionSummary?: unknown
}

type CachedDiscoveryReport = {
  reportId: string
  commandId: string
  sourceVersion: number
  reportData: DiscoveryReportData
  htmlContent: string
  reportName: string
}

function reportCommandId(prefix: string): string {
  if (!globalThis.crypto?.randomUUID) {
    throw new Error('Canonical report generation requires crypto.randomUUID')
  }
  return `${prefix}-${globalThis.crypto.randomUUID()}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isPrepareAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

function prepareTimeoutError(): Error {
  const timeout = new Error('Report prepare timed out')
  timeout.name = 'ReportPrepareTimeout'
  return timeout
}

/**
 * One prepare request. The signal is the visitor cancel.
 * The timer is this attempt only, so a 409 does not shorten the retry.
 */
async function fetchPrepareAttempt(
  body: string,
  sessionId: string,
  outerSignal: AbortSignal,
): Promise<Response> {
  const attempt = new AbortController()
  const onOuterAbort = () => {
    attempt.abort(outerSignal.reason ?? new DOMException('Aborted', 'AbortError'))
  }
  if (outerSignal.aborted) {
    throw outerSignal.reason ?? new DOMException('Aborted', 'AbortError')
  }
  outerSignal.addEventListener('abort', onOuterAbort, { once: true })
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    attempt.abort(new DOMException('Prepare attempt timed out', 'TimeoutError'))
  }, REPORT_PREPARE_ATTEMPT_TIMEOUT_MS)
  try {
    return await fetch('/api/pdf?action=prepare', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [SESSION_HEADER]: sessionId,
      },
      signal: attempt.signal,
      body,
    })
  } catch (error) {
    if (timedOut) throw prepareTimeoutError()
    throw error
  } finally {
    clearTimeout(timer)
    outerSignal.removeEventListener('abort', onOuterAbort)
  }
}

/**
 * N22: a brief that already completed comes back with the projection the browser
 * holds after a reload (same version). The store rejects an envelope that is not
 * newer, which is not a failure. Another session or a newer rejected one still is.
 */
function alreadyHoldsEnvelope(envelope: unknown, sessionId: string): boolean {
  const held = clientSessionViewStore.getServerProjectionSnapshot()
  return (
    held !== null &&
    held.sessionId === sessionId &&
    isRecord(envelope) &&
    envelope.sessionId === sessionId &&
    Number.isSafeInteger(envelope.version) &&
    (envelope.version as number) <= held.version
  )
}

function parsePreparedReport(value: unknown): {
  reportId: string
  sourceVersion: number
  reportData: DiscoveryReportData
  htmlContent: string
} {
  if (!isRecord(value)) throw new Error('Report preparation returned an invalid response')
  if (
    typeof value.reportId !== 'string' ||
    !Number.isSafeInteger(value.sourceVersion) ||
    !isRecord(value.reportData) ||
    typeof value.htmlContent !== 'string'
  ) {
    throw new Error('Report preparation returned incomplete canonical data')
  }
  return {
    reportId: value.reportId,
    sourceVersion: value.sourceVersion as number,
    reportData: value.reportData as unknown as DiscoveryReportData,
    htmlContent: value.htmlContent,
  }
}

export function useReportGeneration({
  sessionId,
  setTranscript,
  showToast,
  handleSendMessage,
}: UseReportGenerationArgs) {
  const { t, locale } = useLocaleContext()
  const gateToast = useCallback(
    (missing: readonly string[]) =>
      formatReportGateToast(
        (key, options) => (options ? String(t(key, options)) : String(t(key))),
        missing,
      ),
    [t],
  )
  const [isPreparingReport, setIsPreparingReport] = useState(false)
  const [prepareTerminal, setPrepareTerminal] = useState<BriefPrepareTerminal | null>(null)
  const [gateNotice, setGateNotice] = useState<string | null>(null)
  const latestReportDataRef = useRef<CachedDiscoveryReport | null>(null)
  /** One in-flight prepare per session so Generate + Live trigger_report share a single brief. */
  const prepareInFlightRef = useRef<Promise<CachedDiscoveryReport> | null>(null)
  const prepareAbortRef = useRef<AbortController | null>(null)
  const userAbortRef = useRef(false)

  const acceptedVersion = useCallback((): number => {
    const envelope = clientSessionViewStore.getServerProjectionSnapshot()
    if (!envelope || envelope.sessionId !== sessionId) {
      throw new Error('A committed session projection is required before generating a report')
    }
    return envelope.version
  }, [sessionId])

  const prepareCanonicalReport = useCallback(async (): Promise<CachedDiscoveryReport> => {
    const cached = latestReportDataRef.current
    if (cached?.reportData.sessionId === sessionId) {
      const liveCompany =
        clientSessionViewStore.getServerProjectionSnapshot()?.projection?.company?.name
      const cachedCompany = cached.reportData.client.company
      if (!liveCompany || !cachedCompany || liveCompany === cachedCompany) {
        return cached
      }
      latestReportDataRef.current = null
    }
    if (prepareInFlightRef.current) {
      return prepareInFlightRef.current
    }

    const run = async (): Promise<CachedDiscoveryReport> => {
    const reportId = reportCommandId('report')
    const commandId = reportCommandId('report-request')
    const controller = new AbortController()
    prepareAbortRef.current = controller
    userAbortRef.current = false
    await ensureSessionCapability(sessionId)

    let payload: unknown = {}
    let nextExpectedVersion: number | null = null
    const maxAttempts = SESSION_VERSION_RETRY_LIMIT + 1
    try {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (controller.signal.aborted) throw controller.signal.reason ?? new DOMException('Aborted', 'AbortError')
      const expectedVersion = nextExpectedVersion ?? acceptedVersion()
      const response = await fetchPrepareAttempt(
        JSON.stringify({
          sessionId,
          reportId,
          commandId,
          expectedVersion,
          uiLocale: locale,
          currentInformation: true,
        }),
        sessionId,
        controller.signal,
      )
      payload = await response.json().catch(() => ({}))
      if (response.ok) {
        if (!isRecord(payload) || payload.projectionEnvelope === undefined) {
          throw new Error('Prepared report is missing a session projection envelope')
        }
        const preparedNow = parsePreparedReport(payload)
        if (preparedNow.reportData.sessionId !== sessionId) {
          throw new Error('Prepared report does not match the active session')
        }
        cachePreparedDiscoveryReport({
          sessionId,
          htmlContent: preparedNow.htmlContent,
          reportItem: createDiscoveryReportTranscriptItem(
            preparedNow.reportData,
            preparedNow.htmlContent,
            undefined,
            locale,
          ),
        })
        if (
          !acceptEmbeddedSessionProjection(payload, 'POST /api/pdf?action=prepare') &&
          !alreadyHoldsEnvelope(payload.projectionEnvelope, sessionId)
        ) {
          clearCachedDiscoveryReport()
          throw new Error('Failed to accept the prepared report session projection')
        }
        break
      }
      acceptEmbeddedSessionProjection(payload, 'POST /api/pdf?action=prepare')
      const record = isRecord(payload) ? payload : null
      const envelopeVersion =
        record && isRecord(record.projectionEnvelope) && typeof record.projectionEnvelope.version === 'number'
          ? record.projectionEnvelope.version
          : null
      if (response.status === 409 && attempt < maxAttempts - 1) {
        nextExpectedVersion = envelopeVersion
        await new Promise((resolve, reject) => {
          const timer = setTimeout(resolve, 80 * (attempt + 1))
          if (controller.signal.aborted) {
            clearTimeout(timer)
            reject(controller.signal.reason ?? new DOMException('Aborted', 'AbortError'))
            return
          }
          controller.signal.addEventListener(
            'abort',
            () => {
              clearTimeout(timer)
              reject(controller.signal.reason ?? new DOMException('Aborted', 'AbortError'))
            },
            { once: true },
          )
        })
        continue
      }
      const isGate = record?.code === DOSSIER_GATE_CODE
      if (isGate) throw new ReportDossierGateError(readGateMissing(record?.missing))
      const error = new Error('Failed to prepare report')
      error.name = 'ReportPrepareFailed'
      throw error
    }

    const prepared = parsePreparedReport(payload)
    if (prepared.reportData.sessionId !== sessionId) {
      throw new Error('Prepared report does not match the active session')
    }
    const reportItem = createDiscoveryReportTranscriptItem(
      prepared.reportData,
      prepared.htmlContent,
      undefined,
      locale,
    )
    const cached: CachedDiscoveryReport = {
      reportId: prepared.reportId,
      commandId,
      sourceVersion: acceptedVersion(),
      reportData: prepared.reportData,
      htmlContent: prepared.htmlContent,
      reportName: reportItem.attachment?.name || t('report.defaultName'),
    }
    latestReportDataRef.current = cached
    setPrepareTerminal(null)
    setGateNotice(null)
    return cached
    } catch (error) {
      if (error instanceof Error && error.name === 'ReportPrepareTimeout') {
        setPrepareTerminal('timed_out')
        throw error
      }
      if (isPrepareAbort(error) || controller.signal.aborted) {
        setPrepareTerminal(userAbortRef.current ? 'aborted' : 'timed_out')
        const aborted = new Error(
          userAbortRef.current ? 'Report prepare aborted' : 'Report prepare timed out',
        )
        aborted.name = userAbortRef.current ? 'ReportPrepareAborted' : 'ReportPrepareTimeout'
        throw aborted
      }
      if (error instanceof ReportDossierGateError) {
        setPrepareTerminal('deferred')
      } else {
        setPrepareTerminal('failed')
      }
      throw error
    } finally {
      if (prepareAbortRef.current === controller) prepareAbortRef.current = null
    }
    }
    setPrepareTerminal(null)
    setIsPreparingReport(true)
    prepareInFlightRef.current = run().finally(() => {
      prepareInFlightRef.current = null
      setIsPreparingReport(false)
    })
    return prepareInFlightRef.current
  }, [acceptedVersion, locale, sessionId, t])

  const showReportFailure = useCallback((notice: string) => {
    setTranscript(previous => {
      const lastIndex = previous.length - 1
      const last = previous[lastIndex]
      if (last?.role === 'model' && looksLikeDiscoveryReportPromise(last.text)) {
        return [...previous.slice(0, lastIndex), { ...last, text: notice, isFinal: true, status: 'complete' }]
      }
      return [
        ...previous,
        {
          id: `report-failure-${Date.now()}`,
          role: 'model',
          text: notice,
          timestamp: new Date(),
          isFinal: true,
          status: 'complete',
        },
      ]
    })
    setGateNotice(notice)
    showToast(notice, 'error')
  }, [setTranscript, showToast])

  const placeDiscoveryReportCard = useCallback((prepared: CachedDiscoveryReport) => {
    const reportItem = createDiscoveryReportTranscriptItem(
      prepared.reportData,
      prepared.htmlContent,
      undefined,
      locale,
    )
    setTranscript(previous => {
      if (previous.some(item => item.attachment?.type === 'discovery_report')) {
        return previous
      }
      const lastIndex = previous.length - 1
      const last = previous[lastIndex]
      const openRow = last?.role === 'model' && (last.isFinal === false || last.status === 'streaming')
      // N12: only a finished «generating your brief» promise row is replaced. A live
      // streaming row stays (N46b); folding the card into it drops the card when the turn ends.
      if (
        !openRow &&
        last?.role === 'model' &&
        last.attachment?.type !== 'discovery_report' &&
        reportItem.attachment &&
        (!last.text?.trim() || looksLikeDiscoveryReportPromise(last.text))
      ) {
        const merged: TranscriptItem = {
          ...last,
          text: reportItem.text,
          attachment: reportItem.attachment,
          isFinal: true,
          status: 'complete',
        }
        return [...previous.slice(0, lastIndex), merged]
      }
      return [...previous, reportItem]
    })
  }, [locale, setTranscript])

  /** Resolves true when the brief card is in the chat (#317 317d: the browser then stores the ask). */
  const handleGenerateDiscoveryReport = useCallback(async (
    _summaryAgentOutput?: SummaryAgentOutput,
    source: DiscoveryReportTrigger = 'click',
  ): Promise<boolean> => {
    if (prepareInFlightRef.current) {
      if (source !== 'click') {
        try {
          const prepared = await prepareInFlightRef.current
          placeDiscoveryReportCard(prepared)
          return true
        } catch {
          return false
        }
      }
      const notice = String(t('toast.reportPrepareInFlight'))
      setGateNotice(notice)
      showToast(notice, 'info')
      return false
    }
    try {
      const prepared = await prepareCanonicalReport()
      placeDiscoveryReportCard(prepared)
      showToast(t('toast.reportGenerated'), 'success')
      return true
    } catch (error) {
      if (error instanceof Error && error.name === 'ReportPrepareAborted') return false
      if (error instanceof ReportDossierGateError) {
        showReportFailure(gateToast(error.missing))
        return false
      }
      logger.error('Failed to generate canonical insights report:', error)
      showReportFailure(String(t('toast.reportGenerateFailed')))
      return false
    }
  }, [gateToast, placeDiscoveryReportCard, prepareCanonicalReport, sessionId, showReportFailure, showToast, t])

  const handleDownloadDiscoveryReport = useCallback(async () => {
    try {
      showToast(t('toast.pdfGenerating'), 'info')
      const prepared = await prepareCanonicalReport()
      await downloadDiscoveryReportPdf({
        sessionId,
        reportId: prepared.reportId,
        commandId: `${prepared.commandId}:download`,
        expectedVersion: acceptedVersion(),
      }, { locale })
      showToast(t('toast.pdfDownloadSuccess'), 'success')
    } catch (error) {
      if (error instanceof Error && error.name === 'ReportPrepareAborted') return
      if (error instanceof ReportDossierGateError) {
        showReportFailure(gateToast(error.missing))
        return
      }
      logger.error('Failed to download canonical report:', error)
      showToast(t('toast.pdfDownloadFailed'), 'error')
    }
  }, [acceptedVersion, gateToast, locale, prepareCanonicalReport, sessionId, showReportFailure, showToast, t])

  const handleEmailDiscoveryReport = useCallback(async () => {
    try {
      showToast(t('toast.sendingReport'), 'info')
      const prepared = await prepareCanonicalReport()
      let recipientEmail = prepared.reportData.client.email
      if (!recipientEmail) {
        const promptedEmail = window.prompt(t('report.emailReportPrompt'))
        if (!promptedEmail || !promptedEmail.includes('@')) {
          showToast(t('toast.emailRequiredReport'), 'error')
          return
        }
        recipientEmail = promptedEmail.trim()
      }

      await ensureSessionCapability(sessionId)
      const response = await fetch('/api/pdf?action=email', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          [SESSION_HEADER]: sessionId,
        },
        body: JSON.stringify({
          sessionId,
          reportId: prepared.reportId,
          commandId: `${prepared.commandId}:email`,
          expectedVersion: acceptedVersion(),
          recipientEmail,
          uiLocale: locale,
        }),
      })
      const payload: unknown = await response.json().catch(() => ({}))
      acceptEmbeddedSessionProjection(payload, 'POST /api/pdf?action=email')
      if (!response.ok) {
        const message = isRecord(payload) && typeof payload.message === 'string'
          ? payload.message
          : t('toast.emailFailedUnknown')
        throw new Error(message)
      }
      showToast(t('toast.reportEmailed', { email: recipientEmail }), 'success')
    } catch (error) {
      if (error instanceof Error && error.name === 'ReportPrepareAborted') return
      if (error instanceof ReportDossierGateError) {
        showReportFailure(gateToast(error.missing))
        return
      }
      logger.error('Failed to email canonical report:', error)
      showToast(t('toast.reportEmailFailed'), 'error')
    }
  }, [acceptedVersion, gateToast, locale, prepareCanonicalReport, sessionId, showReportFailure, showToast, t])

  const abortReportPrepare = useCallback(() => {
    userAbortRef.current = true
    prepareAbortRef.current?.abort()
  }, [])

  const handleGenerateExecutiveMemo = useCallback(() => {
    handleSendMessage(t('report.executiveMemoPrompt'))
    showToast(t('toast.executiveMemoRequested'), 'info')
  }, [handleSendMessage, showToast, t])

  return {
    isPreparingReport,
    prepareTerminal,
    gateNotice,
    abortReportPrepare,
    latestReportDataRef,
    handleGeneratePDF: handleDownloadDiscoveryReport,
    handleEmailPDF: handleEmailDiscoveryReport,
    handleGenerateDiscoveryReport,
    handleGenerateExecutiveMemo,
    handleDownloadDiscoveryReport,
    handleEmailDiscoveryReport,
  }
}
